'use client';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import { Calendar } from '@components/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/shadcn/popover';
import { cn } from '@components/lib/utils';
import { CalendarIcon } from 'lucide-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface DateInputProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  onHelpVisible: (visible: boolean) => void;
  setIsFocused?: (visible: boolean) => void;
  isFocused?: boolean;
  disabled?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  field,
  onChange,
  onHelpVisible,
  setIsFocused,
  disabled = false,
}) => {
  const [_isHelpVisible, setIsHelpVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);

  const getDateFromTimestamp = useCallback(
    (value: string | number | undefined | null): Date | undefined => {
      if (!value) return undefined;

      const numValue = Number(value);
      if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
        return undefined;
      }

      try {
        const dateObj = new Date(numValue * 1000);
        if (isNaN(dateObj.getTime())) {
          return undefined;
        }
        return dateObj;
      } catch (_error) {
        return undefined;
      }
    },
    []
  );

  useEffect(() => {
    const parsedDate = getDateFromTimestamp(field.value as string | number);
    setDate(parsedDate);
  }, [field.value, getDateFromTimestamp]);

  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('wheel', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('wheel', handleScroll, true);
    };
  }, [isOpen]);

  const toUnixTime = useCallback((date: Date): number => {
    return Math.floor(date.getTime() / 1000);
  }, []);

  const handleDateChange = useCallback(
    (selectedDate: Date | undefined) => {
      setDate(selectedDate);

      if (selectedDate) {
        const timestamp = toUnixTime(selectedDate);
        onChange(field, timestamp.toString());
      } else {
        onChange(field, '');
      }
    },
    [field, onChange, toUnixTime]
  );

  const displayValue = useMemo(() => {
    if (date) {
      return dayjs(date).format('YYYY/MM/DD');
    }
    return '';
  }, [date]);

  return (
    <div className="ml-auto relative overflow-visible flex items-center gap-1 justify-between flex-1 w-full z-10 opacity-100">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'pl-2 pr-2 py-0.5 border-l border-form-border',
              'bg-white text-left cursor-pointer truncate',
              'w-full min-w-0 flex items-center gap-1',
              !date ? 'text-xs text-neutral-400 py-1' : 'text-sm',
              'focus-visible:outline-shade-aqua-300',
              'fs-exclude'
            )}
            disabled={disabled}
            onFocus={() => setIsFocused && setIsFocused(true)}
            onBlur={() => setIsFocused && setIsFocused(false)}
          >
            <CalendarIcon className="h-3 w-3 shrink-0 text-neutral-400" />
            <span className="truncate">{displayValue || 'yyyy/mm/dd'}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateChange}
            defaultMonth={date}
            className="rounded-lg border shadow-sm"
          />
        </PopoverContent>
      </Popover>
      <InputDescription
        description={field.description}
        onHelpVisible={(visible) => {
          setIsHelpVisible(visible);
          onHelpVisible(visible);
        }}
      />
    </div>
  );
};

export default DateInput;
